/**
 * Stale-While-Revalidate cache for API responses, backed by localStorage.
 *
 * Why: On flaky mobile networks the public site's API calls (content, site,
 * services) silently failed, causing the page to render with the bundled
 * defaults. That meant the owner's CMS edits did not show up on phones.
 *
 * With this cache:
 *  - First visit: render bundled default → API succeeds → cache + render.
 *  - Next visit: render cached value instantly → kick off background refresh.
 *  - Background failure: keep showing cached value (which is the owner's
 *    real edits, not the bundled defaults).
 */

const PREFIX = "atc.cache.v1.";
// Cached entries older than this are treated as expired (still served once
// while we re-fetch, but considered stale for logging).
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30; // 30 days

function safeGet(key) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > MAX_AGE_MS) {
      // Still return — better stale data than empty page
    }
    return parsed.data ?? null;
  } catch {
    return null;
  }
}

function safeSet(key, data) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(
      PREFIX + key,
      JSON.stringify({ ts: Date.now(), data }),
    );
  } catch {
    // Quota exceeded, private mode, etc — ignore
  }
}

export const cache = {
  get: safeGet,
  set: safeSet,
};
